/**
 * S3 Direct Upload Utility (Frontend Only)
 * Handles direct file uploads to AWS S3 and CloudFront retrieval
 * No backend API required - uses pre-signed URLs
 */

import { API_BASE_URL } from '../config/api';

// CloudFront domain from environment or default
const CLOUDFRONT_DOMAIN = process.env.REACT_APP_CLOUDFRONT_DOMAIN || 'cdn.quicklook.in';

// Token decode function (matching AuthContext encoding)
const decodeToken = (encodedToken) => {
  try {
    return atob(atob(encodedToken)); // Double decode
  } catch (error) {
    console.error('Token decode error:', error);
    return null;
  }
};

/**
 * Generate file name based on type
 * @param {string} type - File type (invoice, profile, kyc, awb, leo, inv, pur)
 * @param {string} identifier - AWB number or username
 * @param {string} extension - File extension (pdf, jpg, png)
 * @returns {string} Generated file name
 */
const generateFileName = (type, identifier, extension) => {
  const sanitizedId = identifier.replace(/[^a-zA-Z0-9]/g, '_');
  const timestamp = Date.now();
  
  switch (type) {
    case 'invoice':
      return `invoice_${sanitizedId}.${extension}`;
    case 'pur':
      return `pur_${sanitizedId}.${extension}`;
    case 'profile':
      return `${sanitizedId}_pic.${extension}`;
    case 'kyc':
    case 'promiseLetter':
    case 'bankLetter':
    case 'serviceAggre':
      return `${type}_${sanitizedId}.${extension}`;
    case 'awb':
    case 'leo':
    case 'inv':
      return `${type}_${sanitizedId}.${extension}`;
    default:
      return `${type}_${sanitizedId}_${timestamp}.${extension}`;
  }
};

/**
 * Get folder path based on file type
 * @param {string} type - File type
 * @returns {string} S3 folder path
 */
const getFolderPath = (type) => {
  const folderMap = {
    // All shipment PDFs go to awb-pdf-uploads
    'invoice': 'awb-pdf-uploads',
    'pur': 'awb-pdf-uploads',
    'awb': 'awb-pdf-uploads',
    'leo': 'awb-pdf-uploads',
    'inv': 'awb-pdf-uploads',
    // Profile pictures go to profile-pic
    'profile': 'profile-pic',
    // All KYC documents go to kyc-docs
    'kyc': 'kyc-docs',
    'promiseLetter': 'kyc-docs',
    'bankLetter': 'kyc-docs',
    'serviceAggre': 'kyc-docs'
  };
  
  return folderMap[type] || 'awb-pdf-uploads';
};

/**
 * Upload file directly to S3 using pre-signed URL
 * @param {File} file - File object to upload
 * @param {string} type - File type (invoice, profile, kyc, awb, leo, inv, pur)
 * @param {string} identifier - AWB number or username
 * @returns {Promise<{success: boolean, s3Key?: string, url?: string, error?: string}>}
 */
export const uploadToS3 = async (file, type, identifier) => {
  try {
    if (!file) {
      return { success: false, error: 'No file provided' };
    }

    // Validate file size (50MB limit)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      return { success: false, error: 'File size exceeds 50MB limit' };
    }

    // Get file extension
    const extension = getFileExtension(file.name);
    
    // Generate file name
    const fileName = generateFileName(type, identifier, extension);
    
    console.log('📤 Uploading to S3:', { fileName, type, identifier });

    // Step 1: Get pre-signed URL from backend
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId') || identifier;
    const folderPath = getFolderPath(type);
    
    const requestBody = {
      userId: userId,
      files: [{
        fileName: fileName,
        contentType: file.type,
        folder: folderPath  // Tell backend which folder to use
      }]
    };
    
    console.log('📤 Request to backend:', requestBody);
    
    const presignResponse = await fetch(`${API_BASE_URL}/s3/upload-url`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!presignResponse.ok) {
      throw new Error('Failed to get pre-signed URL');
    }

    const result = await presignResponse.json();
    
    console.log('📥 Backend response:', result);
    
    if (!result.success || !result.data || result.data.length === 0) {
      throw new Error('Invalid response from server');
    }

    const uploadData = result.data[0];
    console.log('📁 S3 Key from backend:', uploadData.key);

    // Step 2: Upload directly to S3 using pre-signed URL
    const uploadResponse = await fetch(uploadData.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload to S3');
    }

    console.log('✅ Upload successful:', { 
      s3Key: uploadData.key, 
      url: getCloudFrontUrl(uploadData.key) 
    });

    return {
      success: true,
      s3Key: uploadData.key,
      url: getCloudFrontUrl(uploadData.key), // Construct URL from key, not publicUrl
      fileName: uploadData.originalFileName
    };
  } catch (error) {
    console.error('❌ S3 Upload Error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed'
    };
  }
};

/**
 * Upload multiple files to S3
 * @param {Array<{file: File, type: string, identifier: string}>} files - Array of file objects
 * @returns {Promise<{success: boolean, results?: Array, error?: string}>}
 */
export const uploadMultipleToS3 = async (files) => {
  try {
    if (!files || files.length === 0) {
      return { success: false, error: 'No files provided' };
    }

    // Validate all files first
    const maxSize = 50 * 1024 * 1024;
    for (const { file } of files) {
      if (file.size > maxSize) {
        return { success: false, error: `File ${file.name} exceeds 50MB limit` };
      }
    }

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId') || files[0].identifier;

    // Prepare files array for backend
    const filesData = files.map(({ file, type, identifier }) => {
      const extension = getFileExtension(file.name);
      const fileName = generateFileName(type, identifier, extension);
      const folderPath = getFolderPath(type);
      return {
        fileName: fileName,
        contentType: file.type,
        folder: folderPath,  // Add folder path
        originalFile: file // Keep reference for upload
      };
    });

    console.log('📤 Uploading multiple files to S3:', filesData.length);

    const requestBody = {
      userId: userId,
      files: filesData.map(f => ({
        fileName: f.fileName,
        contentType: f.contentType,
        folder: f.folder  // Include folder path
      }))
    };
    
    console.log('📤 Request to backend:', requestBody);

    // Step 1: Get pre-signed URLs for all files
    const presignResponse = await fetch(`${API_BASE_URL}/s3/upload-url`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!presignResponse.ok) {
      throw new Error('Failed to get pre-signed URLs');
    }

    const result = await presignResponse.json();
    
    if (!result.success || !result.data || result.data.length === 0) {
      throw new Error('Invalid response from server');
    }

    // Step 2: Upload all files to S3 in parallel
    const uploadPromises = result.data.map((uploadData, index) => {
      const file = filesData[index].originalFile;
      return fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      }).then(response => {
        if (!response.ok) {
          throw new Error(`Failed to upload ${uploadData.originalFileName}`);
        }
        return {
          success: true,
          s3Key: uploadData.key,
          url: getCloudFrontUrl(uploadData.key), // Construct URL from key
          fileName: uploadData.originalFileName
        };
      }).catch(error => {
        return {
          success: false,
          error: error.message,
          fileName: uploadData.originalFileName
        };
      });
    });

    const uploadResults = await Promise.all(uploadPromises);
    
    // Check if all uploads succeeded
    const allSuccess = uploadResults.every(result => result.success);
    const failedUploads = uploadResults.filter(r => !r.success);
    
    console.log('✅ Upload summary:', {
      total: uploadResults.length,
      successful: uploadResults.length - failedUploads.length,
      failed: failedUploads.length
    });

    if (allSuccess) {
      return {
        success: true,
        results: uploadResults.map(r => ({ s3Key: r.s3Key, url: r.url }))
      };
    } else {
      return {
        success: false,
        error: `${failedUploads.length} file(s) failed to upload`,
        results: uploadResults
      };
    }
  } catch (error) {
    console.error('❌ Multiple S3 Upload Error:', error);
    return {
      success: false,
      error: error.message || 'Multiple upload failed'
    };
  }
};

/**
 * Get CloudFront URL from S3 key
 * @param {string} s3Key - S3 object key
 * @returns {string} CloudFront URL
 */
export const getCloudFrontUrl = (s3Key) => {
  if (!s3Key) return '';
  
  // If already a full URL, clean and return
  if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
    // Fix malformed URLs from backend
    let url = s3Key;
    
    // Fix double dots: https://.domain -> https://domain
    url = url.replace('https://.', 'https://');
    url = url.replace('http://.', 'http://');
    
    // Fix /apiusers/ path - extract actual S3 key
    if (url.includes('/apiusers/') || url.includes('/api/users/')) {
      // Extract S3 key from malformed URL
      const match = url.match(/\/(users\/.*?)$/);
      if (match) {
        const extractedKey = match[1];
        return `https://${CLOUDFRONT_DOMAIN}/${extractedKey}`;
      }
    }
    
    return url;
  }
  
  // If CloudFront domain not set, use default
  const domain = CLOUDFRONT_DOMAIN || 'cdn.quicklook.in';
  
  return `https://${domain}/${s3Key}`;
};

/**
 * Delete file from S3
 * @param {string} s3Key - S3 object key to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteFromS3 = async (s3Key) => {
  try {
    if (!s3Key) {
      return { success: false, error: 'No S3 key provided' };
    }

    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/s3/delete`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ key: s3Key })
    });

    const data = await response.json();

    if (response.ok && data.success) {
      console.log('🗑️ File deleted from S3:', s3Key);
      return { success: true };
    } else {
      return {
        success: false,
        error: data.message || 'Delete failed'
      };
    }
  } catch (error) {
    console.error('❌ S3 Delete Error:', error);
    return {
      success: false,
      error: error.message || 'Delete failed'
    };
  }
};

/**
 * Helper to validate file type
 * @param {File} file - File to validate
 * @param {Array<string>} allowedTypes - Allowed MIME types
 * @returns {boolean}
 */
export const validateFileType = (file, allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']) => {
  return allowedTypes.includes(file.type);
};

/**
 * Helper to format file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get file extension from file name
 * @param {string} fileName - File name
 * @returns {string} File extension
 */
export const getFileExtension = (fileName) => {
  const extension = fileName.slice((fileName.lastIndexOf('.') - 1 >>> 0) + 2);
  return extension.toLowerCase();
};

/**
 * Upload Employee Shipment PDFs (AWB, LEO, INV)
 * @param {Object} files - Object with awb, leo, inv File objects
 * @param {string} awbNumber - AWB number for the shipment
 * @returns {Promise<{success: boolean, keys?: Object, error?: string}>}
 */
export const uploadEmployeeShipmentPDFs = async (files, awbNumber) => {
  try {
    if (!files || (!files.awb && !files.leo && (!files.inv || (Array.isArray(files.inv) && files.inv.length === 0)))) {
      return { success: false, error: 'No files provided' };
    }

    if (!awbNumber) {
      return { success: false, error: 'AWB number is required' };
    }

    const token = localStorage.getItem('token');
    
    const leoFiles = Array.isArray(files.leo) ? files.leo.filter(Boolean) : (files.leo ? [files.leo] : []);

    // Support multiple INV PDFs (one by one upload)
    const invFiles = Array.isArray(files.inv) ? files.inv.filter(Boolean) : files.inv ? [files.inv] : [];

    console.log('📤 Requesting pre-signed URLs for employee shipment PDFs');

    // Step 1: Get pre-signed URLs for AWB and LEO (batch), INV (one by one)
    let uploadUrls = {};
    // AWB/LEO batch
    const filesArray = [];
    if (files.awb) {
      filesArray.push({
        type: 'AWB',
        fileName: `AWB_${awbNumber}.pdf`
      });
    }
    if (leoFiles.length === 1) {
      filesArray.push({
        type: 'LEO',
        fileName: `LEO_${awbNumber}.pdf`
      });
    }
    if (filesArray.length > 0) {
      const presignResponse = await fetch(`${API_BASE_URL}/awb/get-upload-urls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          awbNumber: awbNumber,
          uploadCategory: 'uploads',
          files: filesArray
        })
      });
      if (!presignResponse.ok) {
        throw new Error('Failed to get pre-signed URLs');
      }
      const result = await presignResponse.json();
      if (!result.success || !result.data || !result.data.uploadUrls) {
        throw new Error('Invalid response from server');
      }
      uploadUrls = result.data.uploadUrls;
    }

    // Step 2: Upload files to S3 in parallel
    const uploadPromises = [];
    const fileKeys = {};

    if (files.awb && uploadUrls.awb) {
      uploadPromises.push(
        fetch(uploadUrls.awb.uploadUrl, {
          method: 'PUT',
          body: files.awb,
          headers: {
            'Content-Type': files.awb.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload AWB');
          fileKeys.awbPdf = uploadUrls.awb.key;
          return { success: true, type: 'awb' };
        })
      );
    }

    if (leoFiles.length > 0) {
      const leoUploadTargets = [];

      if (Array.isArray(uploadUrls.leo)) {
        leoFiles.forEach((file, index) => {
          if (!uploadUrls.leo[index]) {
            throw new Error('Missing upload URL for LEO PDF');
          }
          leoUploadTargets.push({ file, target: uploadUrls.leo[index] });
        });
      } else if (leoFiles.length === 1 && uploadUrls.leo) {
        leoUploadTargets.push({ file: leoFiles[0], target: uploadUrls.leo });
      } else if (leoFiles.length > 1 && !uploadUrls.leo) {
        // Fallback: request presigned URL per LEO file
        for (let i = 0; i < leoFiles.length; i += 1) {
          const presignResponse = await fetch(`${API_BASE_URL}/awb/get-upload-urls`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              awbNumber: awbNumber,
              uploadCategory: 'uploads',
              files: [
                {
                  type: 'LEO',
                  fileName: `LEO_${awbNumber}_${i + 1}.pdf`
                }
              ]
            })
          });

          if (!presignResponse.ok) {
            throw new Error('Failed to get pre-signed URLs for LEO PDF');
          }

          const result = await presignResponse.json();

          if (!result.success || !result.data || !result.data.uploadUrls || !result.data.uploadUrls.leo) {
            throw new Error('Missing upload URL for LEO PDF');
          }

          leoUploadTargets.push({ file: leoFiles[i], target: result.data.uploadUrls.leo });
        }
      } else {
        leoFiles.forEach((file, index) => {
          const key = `leo_${index + 1}`;
          const target = uploadUrls[key] || uploadUrls[`leo${index + 1}`] || uploadUrls[`leo-${index + 1}`];
          if (!target) {
            throw new Error('Missing upload URL for LEO PDF');
          }
          leoUploadTargets.push({ file, target });
        });
      }

      fileKeys.leoPdfKey = [];
      leoUploadTargets.forEach(({ file, target }) => {
        uploadPromises.push(
          fetch(target.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type
            }
          }).then(response => {
            if (!response.ok) throw new Error('Failed to upload LEO');
            fileKeys.leoPdfKey.push(target.key);
            return { success: true, type: 'leo' };
          })
        );
      });
    }

    // INV upload (one by one)
    fileKeys.invPdf = [];
    for (let i = 0; i < invFiles.length; i++) {
      const file = invFiles[i];
      // Get presigned URL for this INV file
      const presignResponse = await fetch(`${API_BASE_URL}/awb/get-upload-urls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          awbNumber: awbNumber,
          uploadCategory: 'uploads',
          files: [{
            type: 'INV',
            fileName: `INV-PL_${awbNumber}_${i + 1}.pdf`
          }]
        })
      });
      if (!presignResponse.ok) {
        throw new Error('Failed to get pre-signed URL for INV');
      }
      const result = await presignResponse.json();
      if (!result.success || !result.data || !result.data.uploadUrls || !result.data.uploadUrls.inv) {
        throw new Error('Invalid response for INV PDF');
      }
      const target = Array.isArray(result.data.uploadUrls.inv) ? result.data.uploadUrls.inv[0] : result.data.uploadUrls.inv;
      await fetch(target.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });
      fileKeys.invPdf.push(target.key);
    }

    await Promise.all(uploadPromises);

    console.log('✅ All files uploaded successfully:', fileKeys);

    return {
      success: true,
      keys: fileKeys
    };
  } catch (error) {
    console.error('❌ Employee Shipment PDF Upload Error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed'
    };
  }
};

/**
 * Upload Accountant Shipment PDFs (PUR, INVOICE)
 * @param {Object} files - Object with pur, invoice File objects
 * @param {string} awbNumber - AWB number for the shipment
 * @returns {Promise<{success: boolean, keys?: Object, error?: string}>}
 */
export const uploadAccountantShipmentPDFs = async (files, awbNumber) => {
  try {
    if (!files || (!files.pur && !files.invoice)) {
      return { success: false, error: 'No files provided' };
    }

    if (!awbNumber) {
      return { success: false, error: 'AWB number is required' };
    }

    const token = localStorage.getItem('token');
    
    // Prepare files array for backend
    const filesArray = [];
    if (files.pur) {
      filesArray.push({
        type: 'PUR',
        fileName: `PUR_${awbNumber}.pdf`
      });
    }
    if (files.invoice) {
      filesArray.push({
        type: 'INVOICE',
        fileName: `INVOICE_${awbNumber}.pdf`
      });
    }

    console.log('📤 Requesting pre-signed URLs for accountant shipment PDFs');

    // Step 1: Get pre-signed URLs from backend
    const presignResponse = await fetch(`${API_BASE_URL}/awb/get-accountant-upload-urls`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        awbNumber: awbNumber,
        uploadCategory: 'uploads',
        files: filesArray
      })
    });

    if (!presignResponse.ok) {
      throw new Error('Failed to get pre-signed URLs');
    }

    const result = await presignResponse.json();
    
    console.log('📥 Backend response:', result);

    if (!result.success || !result.data || !result.data.uploadUrls) {
      throw new Error('Invalid response from server');
    }

    const uploadUrls = result.data.uploadUrls;

    // Step 2: Upload files to S3 in parallel
    const uploadPromises = [];
    const fileKeys = {};

    if (files.pur && uploadUrls.pur) {
      uploadPromises.push(
        fetch(uploadUrls.pur.uploadUrl, {
          method: 'PUT',
          body: files.pur,
          headers: {
            'Content-Type': files.pur.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload PUR');
          fileKeys.purPdf = uploadUrls.pur.key;
          return { success: true, type: 'pur' };
        })
      );
    }

    if (files.invoice && uploadUrls.invoice) {
      uploadPromises.push(
        fetch(uploadUrls.invoice.uploadUrl, {
          method: 'PUT',
          body: files.invoice,
          headers: {
            'Content-Type': files.invoice.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload INVOICE');
          fileKeys.invoicePdf = uploadUrls.invoice.key;
          return { success: true, type: 'invoice' };
        })
      );
    }

    await Promise.all(uploadPromises);

    console.log('✅ Accountant files uploaded successfully:', fileKeys);

    return {
      success: true,
      keys: fileKeys
    };
  } catch (error) {
    console.error('❌ Accountant Shipment PDF Upload Error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed'
    };
  }
};

/**
 * Upload KYC Documents
 * @param {Object} files - Object with kyc, promiseLetter, bankLetter, serviceAggre File objects
 * @param {string} userId - User ID for KYC documents
 * @returns {Promise<{success: boolean, keys?: Object, error?: string}>}
 */
export const uploadKYCDocuments = async (files, userId) => {
  try {
    if (!files || (!files.kyc && !files.promiseLetter && !files.bankLetter && !files.serviceAggre)) {
      return { success: false, error: 'No files provided' };
    }

    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const token = localStorage.getItem('token');
    
    // Map frontend field names to backend docType names
    const docTypeMapping = {
      kyc: 'kyc_letter',
      promiseLetter: 'promise_letter',
      bankLetter: 'bank_letter',
      serviceAggre: 'service_aggrement'
    };
    
    // Prepare files array for backend
    const filesArray = [];
    if (files.kyc) {
      filesArray.push({
        docType: docTypeMapping.kyc,
        fileName: 'kyc_letter.pdf'
      });
    }
    if (files.promiseLetter) {
      filesArray.push({
        docType: docTypeMapping.promiseLetter,
        fileName: 'promise_letter.pdf'
      });
    }
    if (files.bankLetter) {
      filesArray.push({
        docType: docTypeMapping.bankLetter,
        fileName: 'bank_letter.pdf'
      });
    }
    if (files.serviceAggre) {
      filesArray.push({
        docType: docTypeMapping.serviceAggre,
        fileName: 'service_aggrement.pdf'
      });
    }

    console.log('📤 Requesting pre-signed URLs for KYC documents');

    // Step 1: Get pre-signed URLs from backend
    const presignResponse = await fetch(`${API_BASE_URL}/awb/get-kyc-upload-urls`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: userId,
        uploadCategory: 'kyc',
        files: filesArray
      })
    });

    if (!presignResponse.ok) {
      throw new Error('Failed to get pre-signed URLs for KYC');
    }

    const result = await presignResponse.json();
    
    console.log('📥 Backend response:', result);

    if (!result.success || !result.data || !result.data.uploadUrls) {
      throw new Error('Invalid response from server');
    }

    const uploadUrls = result.data.uploadUrls;

    // Step 2: Upload files to S3 in parallel
    const uploadPromises = [];
    const fileKeys = {};

    if (files.kyc && uploadUrls.kyc_letter) {
      uploadPromises.push(
        fetch(uploadUrls.kyc_letter.uploadUrl, {
          method: 'PUT',
          body: files.kyc,
          headers: {
            'Content-Type': files.kyc.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload KYC Letter');
          fileKeys.kyc = uploadUrls.kyc_letter.key;
          return { success: true, type: 'kyc_letter' };
        })
      );
    }

    if (files.promiseLetter && uploadUrls.promise_letter) {
      uploadPromises.push(
        fetch(uploadUrls.promise_letter.uploadUrl, {
          method: 'PUT',
          body: files.promiseLetter,
          headers: {
            'Content-Type': files.promiseLetter.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload Premise Letter');
          fileKeys.promiseLetter = uploadUrls.promise_letter.key;
          return { success: true, type: 'promise_letter' };
        })
      );
    }

    if (files.bankLetter && uploadUrls.bank_letter) {
      uploadPromises.push(
        fetch(uploadUrls.bank_letter.uploadUrl, {
          method: 'PUT',
          body: files.bankLetter,
          headers: {
            'Content-Type': files.bankLetter.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload Bank Letter');
          fileKeys.bankLetter = uploadUrls.bank_letter.key;
          return { success: true, type: 'bank_letter' };
        })
      );
    }

    if (files.serviceAggre && uploadUrls.service_aggrement) {
      uploadPromises.push(
        fetch(uploadUrls.service_aggrement.uploadUrl, {
          method: 'PUT',
          body: files.serviceAggre,
          headers: {
            'Content-Type': files.serviceAggre.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload Service Aggrement');
          fileKeys.serviceAggre = uploadUrls.service_aggrement.key;
          return { success: true, type: 'service_aggrement' };
        })
      );
    }

    await Promise.all(uploadPromises);

    console.log('✅ KYC documents uploaded successfully:', fileKeys);

    return {
      success: true,
      keys: fileKeys
    };
  } catch (error) {
    console.error('❌ KYC Documents Upload Error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed'
    };
  }
};

/**
 * Upload Sea Export Shipment PDFs (BL, LEO, INV_PL)
 * @param {Object} files - Object with blPdf, leoPdf, invPlPdf File objects
 * @param {string} blNumber - B/L number for the shipment
 * @returns {Promise<{success: boolean, keys?: Object, error?: string}>}
 */
export const uploadSeaExportShipmentPDFs = async (files, blNumber) => {
  try {
    if (!files || (!files.blPdf && !files.leoPdf && !files.invPlPdf)) {
      return { success: false, error: 'No files provided' };
    }

    if (!blNumber) {
      return { success: false, error: 'B/L number is required' };
    }

    const encodedToken = localStorage.getItem('token');
    const token = decodeToken(encodedToken);
    
    if (!token) {
      return { success: false, error: 'Invalid or missing authentication token' };
    }
    
    // Prepare files array for backend with standardized names
    const filesArray = [];
    if (files.blPdf) {
      filesArray.push({
        type: 'BL',
        fileName: `BL_${blNumber}.pdf`
      });
    }
    if (files.leoPdf) {
      filesArray.push({
        type: 'LEO',
        fileName: `LEO_${blNumber}.pdf`
      });
    }
    if (files.invPlPdf) {
      filesArray.push({
        type: 'INV_PL',
        fileName: `INV_PL_${blNumber}.pdf`
      });
    }

    console.log('📤 Requesting pre-signed URLs for sea export shipment PDFs:', {
      blNumber,
      files: filesArray
    });

    // Step 1: Get pre-signed URLs from backend
    const presignResponse = await fetch(`${API_BASE_URL}/sea-export/get-upload-urls`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        blNumber: blNumber,
        files: filesArray
      })
    });

    if (!presignResponse.ok) {
      const errorText = await presignResponse.text();
      console.error('❌ Pre-signed URL request failed:', errorText);
      throw new Error('Failed to get pre-signed URLs');
    }

    const result = await presignResponse.json();
    
    console.log('📥 Backend response:', JSON.stringify(result, null, 2));

    if (!result.success || !result.data) {
      throw new Error(result.message || 'Invalid response from server');
    }

    const uploadUrls = result.data.uploadUrls || result.data;
    console.log('📁 Upload URLs:', uploadUrls);

    // Step 2: Upload files to S3 in parallel
    const uploadPromises = [];
    const fileKeys = {};

    // Handle BL PDF
    if (files.blPdf && uploadUrls.blPdf) {
      uploadPromises.push(
        fetch(uploadUrls.blPdf.uploadUrl, {
          method: 'PUT',
          body: files.blPdf,
          headers: {
            'Content-Type': files.blPdf.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload B/L PDF');
          fileKeys.blPdf = uploadUrls.blPdf.key;
          console.log('✅ B/L PDF uploaded:', uploadUrls.blPdf.key);
          return { success: true, type: 'blPdf' };
        })
      );
    }

    // Handle LEO PDF
    if (files.leoPdf && uploadUrls.leoPdf) {
      uploadPromises.push(
        fetch(uploadUrls.leoPdf.uploadUrl, {
          method: 'PUT',
          body: files.leoPdf,
          headers: {
            'Content-Type': files.leoPdf.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload LEO PDF');
          fileKeys.leoPdf = uploadUrls.leoPdf.key;
          console.log('✅ LEO PDF uploaded:', uploadUrls.leoPdf.key);
          return { success: true, type: 'leoPdf' };
        })
      );
    }

    // Handle INV_PL PDF
    if (files.invPlPdf && uploadUrls.invPlPdf) {
      uploadPromises.push(
        fetch(uploadUrls.invPlPdf.uploadUrl, {
          method: 'PUT',
          body: files.invPlPdf,
          headers: {
            'Content-Type': files.invPlPdf.type
          }
        }).then(response => {
          if (!response.ok) throw new Error('Failed to upload INV/PL PDF');
          fileKeys.invPlPdf = uploadUrls.invPlPdf.key;
          console.log('✅ INV/PL PDF uploaded:', uploadUrls.invPlPdf.key);
          return { success: true, type: 'invPlPdf' };
        })
      );
    }

    await Promise.all(uploadPromises);

    console.log('✅ All sea export files uploaded successfully:', fileKeys);

    return {
      success: true,
      keys: fileKeys
    };
  } catch (error) {
    console.error('❌ Sea Export Shipment PDF Upload Error:', error);
    return {
      success: false,
      error: error.message || 'Upload failed'
    };
  }
};
